/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * OrganizerAttachPanel — attaching runs, in place on the board.
 *
 * ON THE BOARD, NOT IN AN ORGANIZER SECTION. An organizer looking at their own
 * race should not have to leave it, find an admin area and look the race up
 * again. Same page, same URL as everybody else's; the panel simply is not there
 * for anybody else.
 *
 * NOTHING HERE DECIDES WHO MAY SEE IT. The saved query behind `fetchAttachables`
 * answers rows only to the caller who created the race, so a visitor and an
 * ordinary athlete get an empty list and this draws nothing at all. Do not add
 * a role check here and treat it as the guard — the server is the guard.
 *
 * THE ONE EXCEPTION IS `signedIn`, and it is not a permission check. It stops
 * the panel ASKING at all when nobody is logged in: the query sits behind the
 * signed-in endpoint, and a 403 there raises a "Session expired" dialog over
 * the whole page. On a public board that dialog met every visitor.
 *
 * WHICH RUN IS OFFERED. The fastest one that cleared the category distance.
 * Qualifying is on distance, ranking is on time — extra distance an athlete
 * chose to run earns no better place. The organizer can pick a different run.
 */

import React, { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchAttachables, bestRunFor, attachMany, fetchKnownNames, titleCase,
} from './attachApi';
import { fetchTimingSources, timingSourceIdByCode, APPLICATION_TRACKED } from './resultsApi';
import { formatClock, formatKm } from './boardModel';
import ChronoBusy from './ChronoBusy';
import { fetchAllRaceResults } from './boardApi';
import { idOf } from './boardIndexModel';
import { removeResult } from './publishApi';

const INK = '#241708';
const MUTED = '#6B5844';
const ACCENT = '#C2410C';
const HAIR = 'rgba(36, 23, 8, .10)';

/**
 * Entrants drawn at once.
 *
 * Not a batch size — see ATTACH_BATCH in attachApi, which is set by the
 * pre-save hook's timeout and is a different number for a different reason.
 * This one is only about how much of a race fits on a screen.
 */
const PER_PAGE = 30;

const mono = { fontVariantNumeric: 'tabular-nums' };

/** Previous/Next, which look the same apart from being spent. */
function pagerStyle(spent) {
  return {
    minHeight: 38, padding: '0 14px', borderRadius: 9,
    border: `1px solid ${HAIR}`, background: '#fff',
    color: spent ? '#A69683' : INK, fontSize: 13, fontWeight: 600,
    cursor: spent ? 'not-allowed' : 'pointer',
  };
}

/** "2 Nov, 6:00 am", or '' when there is no time to show. */
function whenOf(value) {
  if (!value) return '';
  const at = new Date(value);
  if (Number.isNaN(at.getTime())) return '';
  return at.toLocaleString(undefined, {
    day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit',
  });
}

/**
 * What to call this athlete.
 *
 * Their own name from a past result first — it is the name already on the
 * board and the one they chose — then their profile handle, and only then a
 * placeholder. A placeholder is never written onto a result: see onAttach.
 */
function nameOf(person, knownNames) {
  const known = knownNames && knownNames.get(String(person.athleteUserId));
  if (known) return known;
  if (person.athleteHandle) return titleCase(person.athleteHandle);
  return `Athlete ${person.athleteUserId}`;
}

export default function OrganizerAttachPanel({
  api, eventActivityId, minDistanceMeters = 0, signedIn = false,
}) {
  const queryClient = useQueryClient();
  const [chosen, setChosen] = useState(() => new Map());
  const [busy, setBusy] = useState(false);
  // { done, total } while attaching — fed by attachMany, batch by batch.
  const [progress, setProgress] = useState(null);
  const [report, setReport] = useState(null);
  // Which just-attached result is armed for removal, and which is mid-flight.
  const [undoing, setUndoing] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const attachable = useQuery({
    queryKey: ['board', 'attachable', eventActivityId, minDistanceMeters],
    queryFn: ({ signal }) => fetchAttachables(api, { eventActivityId, minDistanceMeters, signal }),
    // NEVER ASK WITHOUT A SESSION. This is a saved query behind the signed-in
    // endpoint, and a visitor with no session gets a 403 that the API helper
    // turns into a "Session expired" dialog across the whole page. The board is
    // public, so most people reading it are exactly that visitor — the panel
    // must stay silent for them rather than interrogating the server.
    enabled: Number.isFinite(eventActivityId) && signedIn,
  });

  const sources = useQuery({
    queryKey: ['board', 'timing-sources'],
    queryFn: ({ signal }) => fetchTimingSources(api, { signal }),
    // Only wanted once there is something to attach — a visitor never asks.
    enabled: (attachable.data || []).length > 0,
  });

  // The names, read from these athletes' own past results — see fetchKnownNames.
  const names = useQuery({
    queryKey: ['board', 'attach-names', eventActivityId, (attachable.data || []).length],
    queryFn: ({ signal }) => fetchKnownNames(api, {
      userIds: (attachable.data || []).map((p) => p.athleteUserId),
      signal,
    }),
    enabled: (attachable.data || []).length > 0,
  });
  // Both memoised, not because the fallback is expensive but because
  // `|| new Map()` and `|| []` mint a fresh object on EVERY render, and the
  // lists below depend on them — without this the search and the page would be
  // recomputed on every keystroke anywhere on the board.
  const knownNames = useMemo(() => names.data || new Map(), [names.data]);
  const people = useMemo(() => attachable.data || [], [attachable.data]);
  const withRuns = useMemo(() => people.filter((p) => p.runs.length > 0), [people]);
  const withoutRuns = people.length - withRuns.length;

  /**
   * Who the SEARCH leaves, and then who THIS PAGE shows.
   *
   * Both narrow the list that is DRAWN and neither touches what gets attached.
   * A thousand-entrant race is why this exists — a thousand rows each carrying
   * a dropdown is a page nobody can use — but an organizer who pressed
   * "Attach 920 runs" after typing a name, and got 12, would rightly call that
   * a bug. So `onAttach` reads `withRuns`, never these.
   */
  const matching = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return withRuns;
    return withRuns.filter((person) => (
      nameOf(person, knownNames).toLowerCase().includes(needle)
      || String(person.athleteHandle || '').toLowerCase().includes(needle)
    ));
  }, [withRuns, search, knownNames]);

  const pageCount = Math.max(1, Math.ceil(matching.length / PER_PAGE));
  // A search that shortens the list can leave the reader stranded on page 9 of
  // 2, which draws an empty panel that looks broken. Clamp rather than reset:
  // paging back from a narrowed list should land somewhere real.
  const current = Math.min(page, pageCount);
  const shown = useMemo(
    () => matching.slice((current - 1) * PER_PAGE, current * PER_PAGE),
    [matching, current],
  );

  // Nothing to show: not your race, or everybody already has a result.
  //
  // UNLESS A REPORT IS UP. Attaching the last candidate empties this list, and
  // the panel used to vanish the instant it succeeded — taking the result
  // report and its undo list with it. That hit hardest in the case the undo
  // exists for: attaching ONE athlete whose run was picked wrongly left
  // nothing on screen at all, so the only way back was the organizer screen.
  // The report outlives the list it came from.
  if (!attachable.isLoading && people.length === 0 && !report) return null;
  if (attachable.isLoading && !report) return null;

  const timingSourceId = timingSourceIdByCode(sources.data || [], APPLICATION_TRACKED);

  function runFor(person) {
    const picked = chosen.get(String(person.registrationId));
    if (picked === undefined) return bestRunFor(person);
    if (picked === '') return null;
    return person.runs.find((r) => String(r.id) === String(picked)) || null;
  }

  function pick(person, value) {
    setChosen((current) => {
      const next = new Map(current);
      next.set(String(person.registrationId), value);
      return next;
    });
  }

  /**
   * Undo one attach that picked the wrong run.
   *
   * Removes the result and takes that person off the undo list, so the list
   * only ever offers what is still undoable. Invalidating the board queries
   * puts them back in the entrant list above with every run they recorded —
   * the panel's rule is "entrants with no result yet", so nothing else has to
   * know this happened.
   */
  async function onUndo(entry) {
    setRemoving(entry.resultId);
    try {
      await removeResult(api, { resultId: entry.resultId });
      setUndoing(null);
      setReport((current) => (current
        ? {
          ...current,
          undoable: (current.undoable || []).filter((e) => e.resultId !== entry.resultId),
          attached: current.attached.filter(
            (id) => String(id) !== String(entry.registrationId),
          ),
        }
        : current));
      queryClient.invalidateQueries({ queryKey: ['board'] });
    } catch (e) {
      setReport((current) => (current
        ? {
          ...current,
          refused: [
            ...current.refused,
            { athleteName: entry.name, reason: (e && e.message) || 'Could not remove that result.' },
          ],
        }
        : current));
    } finally {
      setRemoving(null);
    }
  }

  async function onAttach() {
    const picks = withRuns
      .map((person) => ({ person, run: runFor(person) }))
      .filter((entry) => entry.run)
      .map((entry) => ({
        registrationId: entry.person.registrationId,
        // Only a real name is written onto the result — the board is public and
        // "Athlete 12" under a podium would be worse than no name at all.
        athleteName: knownNames.get(String(entry.person.athleteUserId))
          || (entry.person.athleteHandle ? titleCase(entry.person.athleteHandle) : ''),
        run: entry.run,
      }));
    if (picks.length === 0 || !timingSourceId) return;

    setBusy(true);
    setReport(null);
    setProgress({ done: 0, total: picks.length });
    try {
      const out = await attachMany(api, {
        picks, eventActivityId, timingSourceId, onProgress: setProgress,
      });

      // WHO WE JUST ATTACHED, with the result row each one became.
      //
      // So that a wrong pick can be undone HERE, a moment after it was made,
      // instead of sending the organizer to the organizer screen to find one
      // name among thousands. attachMany reports registration ids, not result
      // ids, so the rows are read back — one or two requests — and matched up.
      // Read back rather than taken from the write's response because the
      // batch endpoint's reply shape is not something this code should depend
      // on, and a re-read is true whatever happened in between.
      let undoable = [];
      try {
        const byRegistration = new Map();
        const fresh = await fetchAllRaceResults(api, { eventActivityId });
        fresh.rows.forEach((row) => {
          const key = String(idOf(row.registration_id));
          byRegistration.set(key, row);
        });
        const nameFor = new Map(picks.map((p) => [String(p.registrationId), p.athleteName]));
        undoable = out.attached
          .map((registrationId) => {
            const row = byRegistration.get(String(registrationId));
            return row
              ? {
                registrationId,
                resultId: row.id,
                name: nameFor.get(String(registrationId)) || row.athlete_name || 'This athlete',
              }
              : null;
          })
          .filter(Boolean);
      } catch (e) {
        // The attach itself succeeded; only the undo list is missing. Say
        // nothing — an error here would read as the attach having failed.
        undoable = [];
      }

      setReport({ ...out, undoable });
      // The board, its counts and this panel all moved.
      queryClient.invalidateQueries({ queryKey: ['board'] });
    } catch (e) {
      setReport({ attached: [], refused: [{ reason: (e && e.message) || 'Could not attach.' }] });
    } finally {
      setBusy(false);
      setProgress(null);
    }
  }

  const ready = withRuns.filter((person) => runFor(person)).length;

  return (
    <section
      aria-label="Attach runs to this race"
      style={{
        border: `1px solid ${HAIR}`, borderRadius: 18, padding: '18px 20px',
        marginBottom: 20, background: '#FFFCF9',
      }}
    >
      <h2 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 700, color: INK }}>
        Attach runs
      </h2>
      <p style={{ margin: '0 0 14px', fontSize: 13, color: MUTED, lineHeight: 1.6 }}>
        {withRuns.length > 0
          ? 'These entrants have no result yet. The fastest run that covered the distance is chosen for each — change it if you need to, then attach.'
          : 'Everybody still waiting for a result has no run we can use yet.'}
        {withoutRuns > 0 && withRuns.length > 0 && (
          <>
            {' '}
            <span>
              {withoutRuns === 1
                ? '1 other entrant has no run we can use yet.'
                : `${withoutRuns} other entrants have no run we can use yet.`}
            </span>
          </>
        )}
      </p>

      {report && (
        <div
          role="status"
          style={{
            padding: '10px 12px', borderRadius: 10, marginBottom: 14, fontSize: 13,
            background: report.refused.length ? '#FFF1E8' : '#ECFDF3',
            color: report.refused.length ? '#8A2C06' : '#1B7F48',
          }}
        >
          {report.attached.length} attached
          {report.refused.length > 0 && `, ${report.refused.length} refused`}
          {report.refused.map((bad, i) => (
            <div key={i} style={{ marginTop: 6 }}>
              {bad.athleteName ? `${bad.athleteName}: ` : ''}{bad.reason}
            </div>
          ))}

          {/* UNDO, WHERE THE MISTAKE WAS MADE.
              The panel picks each entrant's fastest qualifying run and the
              organizer confirms in bulk — on a full page nobody inspects every
              dropdown, so picking the wrong run is the expected slip, not an
              unlikely one. Until now the only way back was the organizer
              screen, which means leaving the board and finding one name among
              thousands. Removing here puts the athlete straight back in the
              list above with all of their runs, ready to pick again.

              Safe to delete now: as of pack 1.0.12 the uniqueness rule on
              registration_id ignores removed rows, so the athlete can be
              attached again. Under the old rule this produced someone who
              could never be re-attached. */}
          {(report.undoable || []).length > 0 && (
            <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid rgba(36,23,8,.12)' }}>
              <div style={{ fontWeight: 700, marginBottom: 2 }}>Picked the wrong run for someone?</div>
              <div style={{ color: MUTED, marginBottom: 8 }}>
                Remove them here and they go back in the list above, with every run they recorded.
              </div>
              <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                {report.undoable.map((entry) => (
                  <div
                    key={entry.resultId}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      gap: 10, padding: '6px 0',
                    }}
                  >
                    <span style={{ color: INK, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {entry.name}
                    </span>
                    <button
                      type="button"
                      onClick={() => (undoing === entry.resultId
                        ? onUndo(entry)
                        : setUndoing(entry.resultId))}
                      onBlur={() => setUndoing((c) => (c === entry.resultId ? null : c))}
                      disabled={busy || removing === entry.resultId}
                      aria-label={undoing === entry.resultId
                        ? `Confirm removing the result for ${entry.name}`
                        : `Remove the result for ${entry.name}`}
                      style={{
                        flex: 'none', minHeight: 32, padding: '0 11px', borderRadius: 8,
                        fontSize: 12.5, fontWeight: 600, cursor: busy ? 'default' : 'pointer',
                        border: `1px solid ${undoing === entry.resultId ? '#B91C1C' : 'rgba(36,23,8,.18)'}`,
                        background: undoing === entry.resultId ? '#B91C1C' : '#fff',
                        color: undoing === entry.resultId ? '#fff' : MUTED,
                      }}
                    >
                      {removing === entry.resultId
                        ? 'Removing…'
                        : (undoing === entry.resultId ? 'Confirm' : 'Remove')}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {withRuns.length > PER_PAGE && (
        <div style={{
          display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap',
          padding: '10px 0 4px', borderTop: `1px solid ${HAIR}`,
        }}
        >
          <label htmlFor="attach-search" style={{ fontSize: 13, color: MUTED }}>Find an entrant</label>
          <input
            id="attach-search"
            type="search"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Name"
            style={{
              minHeight: 38, flex: '1 1 220px', maxWidth: 300, padding: '0 11px',
              borderRadius: 9, border: `1px solid ${HAIR}`, fontSize: 13, color: INK,
            }}
          />
          <span style={{ fontSize: 13, color: MUTED }}>
            {search.trim()
              ? `${matching.length} of ${withRuns.length} entrants`
              : `${withRuns.length} entrants with a run`}
          </span>
        </div>
      )}

      {matching.length === 0 && search.trim() && (
        <p style={{ margin: '12px 0 0', fontSize: 13, color: MUTED, borderTop: `1px solid ${HAIR}`, paddingTop: 12 }}>
          Nobody here matches “{search.trim()}”.
        </p>
      )}

      {shown.map((person) => {
        const run = runFor(person);
        return (
          <div
            key={person.registrationId}
            style={{
              display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
              padding: '10px 0', borderTop: `1px solid ${HAIR}`,
            }}
          >
            <span style={{ fontWeight: 600, color: INK, fontSize: 14, flex: '0 0 160px' }}>
              {nameOf(person, knownNames)}
            </span>
                <label style={{ fontSize: 13, color: MUTED, flex: '1 1 240px', maxWidth: 340 }}>
              <span style={{
                position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)',
              }}
              >
                {`Run to attach for ${nameOf(person, knownNames)}`}
              </span>
              <select
                value={run ? String(run.id) : ''}
                onChange={(event) => pick(person, event.target.value)}
                style={{
                  // A fixed width on purpose. Sized by content, every row's
                  // dropdown came out a different length — see the owner's
                  // screenshot of 2026-11-02 — and a column of ragged controls
                  // reads as broken. Full width on a phone.
                  width: 'min(320px, 100%)',
                  minHeight: 40, padding: '0 10px', borderRadius: 10,
                  border: `1px solid ${HAIR}`, background: '#fff', color: INK, fontSize: 13,
                }}
              >
                {person.runs.map((candidate) => (
                  <option key={candidate.id} value={String(candidate.id)}>
                    {`${formatKm(candidate.distance_meters)} · ${formatClock(candidate.duration_seconds)} · ${whenOf(candidate.started_at)}`}
                  </option>
                ))}
                <option value="">Skip this entrant</option>
              </select>
            </label>
          </div>
        );
      })}

      {pageCount > 1 && (
        <nav
          aria-label="Entrant pages"
          style={{
            display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
            paddingTop: 12, marginTop: 4, borderTop: `1px solid ${HAIR}`,
          }}
        >
          <button
            type="button"
            onClick={() => setPage(Math.max(1, current - 1))}
            disabled={current <= 1}
            style={pagerStyle(current <= 1)}
          >
            Previous
          </button>
          <span style={{ ...mono, fontSize: 13, color: MUTED }}>
            Page {current} of {pageCount}
          </span>
          <button
            type="button"
            onClick={() => setPage(Math.min(pageCount, current + 1))}
            disabled={current >= pageCount}
            style={pagerStyle(current >= pageCount)}
          >
            Next
          </button>
          <span style={{ fontSize: 12, color: MUTED, flex: '1 1 200px' }}>
            Your choices are kept as you page — attaching covers every entrant, not
            just this page.
          </span>
        </nav>
      )}

      {withRuns.length > 0 && (
        <button
          type="button"
          onClick={onAttach}
          disabled={busy || ready === 0 || !timingSourceId}
          style={{
            marginTop: 16, minHeight: 46, padding: '0 20px', border: 0, borderRadius: 11,
            background: ready === 0 || !timingSourceId ? '#D8CEC3' : ACCENT, color: '#fff',
            fontWeight: 700, fontSize: 14,
            cursor: busy || ready === 0 || !timingSourceId ? 'not-allowed' : 'pointer',
          }}
        >
          {/* The label no longer carries the waiting state — ChronoBusy does.
              A button that greys itself and changes its own word is the
              weakest signal a screen can give for a write that takes real
              time; it also leaves the rest of the panel live and clickable
              while rows are going in.
              Was: {busy ? 'Attaching…' : `Attach ${ready} …`} */}
          {`Attach ${ready} ${ready === 1 ? 'run' : 'runs'}`}
        </button>
      )}

      {/* MEASURED AT 500 RUNS: 73 seconds. attachMany writes in batches of 25,
          so it knows exactly where it has got to, and over a minute of work
          behind a dial that cannot say so is barely better than the button
          label this replaced. The bar is fed batch by batch.

          The wording is the organizer's own: this is a write to THEIR race,
          not the board reading somebody else's. */}
      <ChronoBusy
        open={busy}
        title={`Attaching ${progress ? progress.total : ready} ${(progress ? progress.total : ready) === 1 ? 'run' : 'runs'} to this race`}
        note="Each run is being checked and written as an official result. Please leave this page open."
        progress={progress}
      />
    </section>
  );
}
